import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, Beer, Snowflake } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import BlurredPrice, { B2B_REG, B2BUnlockDialog } from "../components/BlurredPrice";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";

const SECTIONS = [
  { key: "fusti", title: "Fusti Cocktail 19L", icon: Beer },
  { key: "granite", title: "Granite & Preparati", icon: Snowflake },
];

function Row({ p }) {
  const isFusto = p.category === "fusti";
  return (
    <div className="flex items-center gap-4 bg-white rounded-2xl border border-stone-200 p-4 hover:shadow-md transition-shadow" data-testid={`listino-public-row-${p.id}`}>
      <img src={p.image_url} alt={p.name} loading="lazy" className="w-16 h-16 rounded-xl object-cover bg-stone-100 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-[#C2593F]">{p.format}</div>
        <h3 className="font-serif text-lg font-semibold text-[#0B2A45] truncate">{p.name}</h3>
        <p className="text-xs text-stone-500 line-clamp-1">{p.description}</p>
      </div>
      <BlurredPrice
        label={isFusto ? "Costo per drink" : "Prezzo operatori"}
        masked={isFusto ? p.drink_cost_masked || "€ X,XX" : p.agent_price_masked || "€ XX,XX"}
        note={isFusto ? `Fusto: ${p.agent_price_masked || "€ XX,XX"}` : null}
        testId={`listino-public-price-${p.id}`}
      />
    </div>
  );
}

export default function ListinoPublic() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);

  useSeo({ title: "Listino HoReCa: fusti cocktail e granite | Guatemalan Spirit",
    description: "Prezzi riservati a bar, lidi e ristoranti: cocktail in fusto Cornelius 19L (Spritz, Gin Tonic) e granite 100% frutta. Registrazione gratuita con P.IVA." });

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] paper-grain" data-testid="listino-public-page">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-[0.7rem] uppercase tracking-[0.2em] font-semibold text-[#C2593F] mb-2">Anteprima listino</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#0B2A45] mb-4">Prezzi riservati agli operatori</h1>
        <p className="text-stone-600 max-w-2xl mb-6">Fusti Cornelius 19L e preparati per granita a prezzi HoReCa. I prezzi sono visibili solo agli operatori registrati con Partita IVA: la registrazione è gratuita e immediata.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#0F4C81] hover:bg-[#0B2A45] rounded-full h-11 px-6" data-testid="listino-public-unlock-btn">
          <Lock className="w-4 h-4 mr-2" /> Sblocca il listino operatori
        </Button>

        {SECTIONS.map(({ key, title, icon: Icon }) => {
          const items = products.filter((p) => p.category === key);
          if (!items.length) return null;
          return (
            <div key={key} className="mt-12" data-testid={`listino-public-section-${key}`}>
              <h2 className="text-base md:text-lg font-semibold text-[#0B2A45] flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-[#0F4C81]" /> {title}</h2>
              <div className="grid gap-3">{items.map((p) => <Row key={p.id} p={p} />)}</div>
            </div>
          );
        })}

        <div className="mt-14 rounded-3xl bg-[#0B2A45] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="listino-public-cta">
          <div>
            <h2 className="font-serif text-2xl font-semibold mb-1">Sei un bar, lido o ristorante?</h2>
            <p className="text-sm text-white/70">Registrati con Partita IVA e accedi subito ai prezzi, al riordino rapido e agli <Link to="/comodato" className="underline underline-offset-2 text-[#FDE047]" data-testid="listino-comodato-link">impianti in comodato</Link>.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button asChild variant="outline" className="rounded-full h-11 px-6 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"><Link to="/comodato" data-testid="listino-comodato-btn">Impianti in comodato</Link></Button>
            <Button asChild className="bg-[#C2593F] hover:bg-[#A84A33] rounded-full h-11 px-6">
              <Link to={B2B_REG} data-testid="listino-public-register-link">Registrati ora <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>
      <Footer />
      <B2BUnlockDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
